import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { CalendarIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useEventCategoriesLookup } from "../api/queries";

export function EventFilter({ hideDateRange = false }: { hideDateRange?: boolean }) {
  const nav = useNavigate({ from: "/app/performance/events" });
  const search = useSearch({
    from: "/app/performance/events",
  }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const titleFilter = findCondition(filters, "title");
  const statusFilter = findCondition(filters, "status");
  const categoryFilter = findCondition(filters, "categoryId");
  const dateFromFilter = filters.find(
    (f) => f.field === "eventDate" && f.op === "gte",
  );
  const dateToFilter = filters.find(
    (f) => f.field === "eventDate" && f.op === "lte",
  );

  const [searchValue, setSearchValue] = useState<string>(
    (titleFilter?.value as string) || "",
  );
  const [dateFrom, setDateFrom] = useState<string>(
    (dateFromFilter?.value as string)
      ? (dateFromFilter!.value as string).slice(0, 10)
      : "",
  );
  const [dateTo, setDateTo] = useState<string>(
    (dateToFilter?.value as string)
      ? (dateToFilter!.value as string).slice(0, 10)
      : "",
  );

  useEffect(
    () => setSearchValue((titleFilter?.value as string) || ""),
    [titleFilter?.value],
  );
  useEffect(
    () =>
      setDateFrom(
        (dateFromFilter?.value as string)
          ? (dateFromFilter!.value as string).slice(0, 10)
          : "",
      ),
    [dateFromFilter?.value],
  );
  useEffect(
    () =>
      setDateTo(
        (dateToFilter?.value as string)
          ? (dateToFilter!.value as string).slice(0, 10)
          : "",
      ),
    [dateToFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "title",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "title");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  function applyDateRange(from: string, to: string) {
    const nextFilters: FilterConditionDTO[] = filters.filter(
      (f) => !(f.field === "eventDate" && (f.op === "gte" || f.op === "lte")),
    );
    if (from) {
      nextFilters.push({
        field: "eventDate",
        op: "gte",
        value: `${from}T00:00:00`,
      });
    }
    if (to) {
      nextFilters.push({
        field: "eventDate",
        op: "lte",
        value: `${to}T23:59:59`,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters.length ? nextFilters : undefined,
      },
    });
  }

  const statusValue =
    typeof statusFilter?.value === "string" ? statusFilter.value : "";
  const categoryLookup = useEventCategoriesLookup();
  const categoryValue =
    typeof categoryFilter?.value === "string" ? categoryFilter.value : "";
  const hasFilter =
    !!titleFilter?.value ||
    !!statusValue ||
    !!categoryValue ||
    !!dateFromFilter ||
    !!dateToFilter;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {/* Search */}
          <Input
            placeholder="ຄົ້ນຫາງານສະແດງ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />

          {/* Status */}
          <SimpleSelect
            value={statusValue || "all"}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-36"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "draft", label: "Draft" },
              { value: "confirmed", label: "ຢືນຢັນ" },
              { value: "completed", label: "ສຳເລັດ" },
              { value: "cancelled", label: "ຍົກເລີກ" },
            ]}
          />

          {/* Category */}
          <SimpleSelect
            value={categoryValue || "all"}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "categoryId");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "categoryId",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ຫມວດ"
            className="h-8 w-full sm:w-44"
            options={[
              { value: "all", label: "ທຸກຫມວດ" },
              ...(categoryLookup.data?.items ?? []).map((item) => ({
                value: item.id,
                label: item.name,
              })),
            ]}
          />

          {/* Date range */}
          {!hideDateRange ? (
          <div className="flex items-center gap-1.5">
            <CalendarIcon className="size-3.5 shrink-0 text-muted-foreground" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                applyDateRange(e.target.value, dateTo);
              }}
              className="h-8 rounded-md border bg-background px-2 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-ring"
            />
            <span className="text-muted-foreground text-xs">—</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => {
                setDateTo(e.target.value);
                applyDateRange(dateFrom, e.target.value);
              }}
              className="h-8 rounded-md border bg-background px-2 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          ) : null}
        </div>

        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setDateFrom("");
              setDateTo("");
              nav({ search: { ...search, filters: undefined, offset: 0 } });
            }}
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
